/**
 * dialogue-lines.ts — R8 #6
 *
 * Post-cutscene dialogue lines per archetype × era.
 * Called after every era-transition or ending cutscene.
 *
 * Layout: LINES[seatIndex][era] → string[]
 *   Seat 0 = Engineer (red)
 *   Seat 1 = Scientist (blue)
 *   Seat 2 = Activist (green)
 *   Seat 3 = Politician (yellow)
 *
 * Era tone guide:
 *   Era 1 (Industrial Dawn)          — raw industrial confidence, growth optimism
 *   Era 2 (Oil Rush)                 — pragmatic oil-era stability, wealth focus
 *   Era 3 (Nuclear & Early Renewables) — transition anxiety, uncomfortable data
 *   Era 4 (Sustainable Future)       — hard-won wisdom, cautious hope
 */

type ArchetypeLines = readonly [
  readonly string[], // era 1
  readonly string[], // era 2
  readonly string[], // era 3
  readonly string[], // era 4
];

// Seat 0 — Engineer (red)
const ENGINEER: ArchetypeLines = [
  // Era 1
  [
    "Smoke means money.",
    "Every chimney stack is progress you can see.",
    "Build fast — worry about cleanup later.",
  ],
  // Era 2
  [
    "Oil is just compressed time.",
    "The machines run smooth. Profits run smoother.",
    "Efficiency now. We'll optimise the rest later.",
  ],
  // Era 3
  [
    "The grid's changing faster than my blueprints.",
    "Nuclear keeps the lights on. Don't let politics kill it.",
    "Every solution creates the next problem. That's engineering.",
  ],
  // Era 4
  [
    "I built things that outlasted me. Some I'm proud of.",
    "Renewable specs are actually tighter — I respect that.",
    "Next generation inherits my infrastructure. Make it good.",
  ],
];

// Seat 1 — Scientist (blue)
const SCIENTIST: ArchetypeLines = [
  // Era 1
  [
    "Correlation between output and soot levels is… troubling.",
    "The models say this is unsustainable. Nobody's reading the models.",
    "Data collected. Conclusions unpopular.",
  ],
  // Era 2
  [
    "CO₂ is at 315 ppm. The trend line is clear.",
    "The oceans are absorbing the heat we pretend isn't there.",
    "Publish. Get ignored. Repeat.",
  ],
  // Era 3
  [
    "The numbers don't lie. We must adapt.",
    "Every degree of warming compounds non-linearly. People hate non-linearly.",
    "Early renewables show promise. Scale is the variable.",
  ],
  // Era 4
  [
    "The models were right. We just didn't want to believe them.",
    "Carbon drawdown is measurable now. That's new.",
    "Science didn't save us — but it told us what would.",
  ],
];

// Seat 2 — Activist (green)
const ACTIVIST: ArchetypeLines = [
  // Era 1
  [
    "The rivers are turning black and they call it growth.",
    "Someone has to say it: this is not worth it.",
    "One day they'll remember we warned them.",
  ],
  // Era 2
  [
    "Oil spills don't make the front page anymore.",
    "Stability for whom? The mangroves aren't stable.",
    "Every pipeline is a promise to someone's grandchildren.",
  ],
  // Era 3
  [
    "Finally — people are listening. Barely.",
    "The transition is real. It's just not fast enough.",
    "Don't let perfect be the enemy of better.",
  ],
  // Era 4
  [
    "Finally — a world worth inheriting.",
    "Every protest, every march — it mattered.",
    "The planet didn't forgive us. It just gave us more time.",
  ],
];

// Seat 3 — Politician (yellow)
const POLITICIAN: ArchetypeLines = [
  // Era 1
  [
    "Full employment. Strong economy. That's the mandate.",
    "My constituents want jobs, not lectures about smoke.",
    "Progress is a campaign promise you can keep.",
  ],
  // Era 2
  [
    "Stability. That's what they want.",
    "Oil revenues fund schools. Fund hospitals. Fund votes.",
    "We balance growth with… responsibility. Eventually.",
  ],
  // Era 3
  [
    "The focus groups are shifting. Green sells now.",
    "I was for renewables before it was popular. Mostly.",
    "The art of governing: knowing when the wind changes.",
  ],
  // Era 4
  [
    "History will be kind to the ones who pivoted early.",
    "The coalition held. Against all odds, the coalition held.",
    "Legacy isn't what you built — it's what you left standing.",
  ],
];

export const ARCHETYPE_DIALOGUE: readonly ArchetypeLines[] = [
  ENGINEER,
  SCIENTIST,
  ACTIVIST,
  POLITICIAN,
] as const;

/** Archetype display names per seat. */
export const ARCHETYPE_NAMES = ["Engineer", "Scientist", "Activist", "Politician"] as const;

/**
 * Get the 3 dialogue lines for a given seat + era.
 * Returns the FIRST of the 3 lines for the ~3s display window
 * (full array available if callers want to cycle sub-lines).
 */
export function getDialogueLine(seat: 0 | 1 | 2 | 3, era: 1 | 2 | 3 | 4): string {
  const lines = ARCHETYPE_DIALOGUE[seat]?.[era - 1];
  if (!lines || lines.length === 0) return "…";
  // Rotate through lines based on era — gives variety across playthroughs.
  return lines[0] ?? "…";
}

/** Get all lines for a seat+era (for use in multi-line speech bubbles). */
export function getAllDialogueLines(seat: 0 | 1 | 2 | 3, era: 1 | 2 | 3 | 4): readonly string[] {
  return ARCHETYPE_DIALOGUE[seat]?.[era - 1] ?? ["…"];
}
